/**
 * TalkTrackPanel — Iter 176 Phase 5 (Danny — scoring_and_dialer_updates.md §5).
 *
 * Quick-reference panel for cold-calling sound bites in B2B healthcare
 * tech outreach. Organized into 7 mini categories per the spec. Each
 * sound bite has a one-click copy button so reps can grab it mid-call.
 *
 * Mounted at the top of /dialer → Scripts tab. Collapsible so it
 * doesn't dominate the screen when reps already know the lines.
 *
 * NOTE: Copy is intentionally hard-coded — these are vetted scripts
 * approved for Enable Healthcare cold calling. Reps should NOT edit
 * them from the UI (that would defeat the whole point of having a
 * canonical reference). A future iteration could persist customised
 * variants per-team via /api/dialer/talk-tracks.
 */
import { useState } from "react";
import { toast } from "sonner";
import {
  Sparkles, Copy, ChevronDown, ChevronUp, Phone, MessageSquare,
  Voicemail, Shield, User as UserIcon, AlertOctagon, Stethoscope,
  Clock,
} from "lucide-react";

// ─── Category model ──────────────────────────────────────────────────
// Each category is { id, title, icon, hint?, sections: [{ heading, lines }] }
// A `line` is just a string (the sound bite). The Copy button copies
// the line verbatim to the clipboard.
const CATEGORIES = [
  {
    id: "opening",
    title: "Opening lines (first 10 seconds)",
    icon: Phone,
    color: "#2563eb",
    hint: "The first 10 seconds determine if the call continues. Never lead with a pitch — convey who you are, that you're not wasting their time, and that you know their world.",
    sections: [
      {
        heading: "With a gatekeeper",
        lines: [
          "Hi, this is [Name] calling from Enable Healthcare — I work with primary care offices in [state] on Medicare billing programs. Is [doctor name] or your office manager available for just two minutes?",
          "Hey — quick question before I let you go. Who handles the billing and operations side of the practice — is that the doctor or do you have an office manager?",
          "I'll be totally upfront — this is a cold call, but I promise it's worth 60 seconds. We help primary care practices bill for Medicare services most aren't collecting on. Is there a better time to reach your administrator?",
        ],
      },
      {
        heading: "With a decision-maker who answers",
        lines: [
          "Dr. [Name] — I'll keep this brief. I work with internal medicine practices in New Jersey. Most of my clients are adding $40–80K a year in Medicare revenue without seeing more patients. Is that something worth a 10-minute call?",
          "Hi, I'm [Name] from Enable Healthcare. We specialize in care management billing for practices your size. I'm not here to sell you anything today — I just want to ask two questions to see if there's a fit.",
        ],
      },
    ],
  },
  {
    id: "gatekeeper",
    title: "Getting through the front desk",
    icon: Shield,
    color: "#0d9488",
    hint: "Treat the front desk as an ally, not an obstacle. Never argue with or dismiss the gatekeeper — their job is to protect the doctor's time.",
    sections: [
      {
        heading: "Establish credibility instantly",
        lines: [
          "I work exclusively with medical practices — not with patients. This is a practice-level business call about Medicare billing programs.",
          "I partner with about 50 primary care offices in the area — I'm not selling anything today, just looking to connect with whoever handles billing decisions.",
        ],
      },
      {
        heading: "Ask for information, not a transfer",
        lines: [
          "I don't need to speak with the doctor — who handles the business side of the practice? Is that you, or do you have an office manager?",
          "Who would be the right person to talk to about Medicare programs that could bring in additional revenue? I want to make sure I reach the right person.",
        ],
      },
      {
        heading: "When told \"the doctor doesn't take sales calls\"",
        lines: [
          "Completely understood — this isn't really a doctor call, it's a billing/revenue call. Who manages that side of the practice?",
          "I hear you — would it make sense to leave my name and a brief message with your office manager? This is about a Medicare program that could add revenue to the practice.",
        ],
      },
      {
        heading: "When told \"send an email / send information\"",
        lines: [
          "I'll absolutely send that over. One quick question before I do — roughly how many Medicare patients does the practice see per month? I want to make sure the information I send is relevant to your patient panel size.",
          "Happy to. What's the best email for your administrator? And is there a specific product you're using for your chronic care management billing, or is that something you haven't looked at yet?",
        ],
      },
      {
        heading: "When the gatekeeper says \"we're not interested\"",
        lines: [
          "I respect that — can I ask, is that because you already have something in place, or just not a priority right now? I want to make sure I'm not calling back at the wrong time.",
          "No problem at all. Quick question — do you know if the practice is currently billing for CCM? Most offices I call aren't, which is usually why I get a chance to speak with them.",
        ],
      },
    ],
  },
  {
    id: "office_manager",
    title: "Talking to an office manager / administrator",
    icon: UserIcon,
    color: "#7c3aed",
    hint: "Office managers care about operational efficiency, staff workload, and revenue — not clinical outcomes. They're often the decision-maker or the champion who carries the pitch to the physician.",
    sections: [
      {
        heading: "Qualification questions (lead with these, not a pitch)",
        lines: [
          "How are you currently handling your chronic care management billing — in-house, outsourced, or not at all?",
          "What's your current EHR setup, and are you happy with how it handles your billing workflow?",
          "Roughly how many Medicare patients do you manage? I want to make sure what I'm sharing is actually relevant to your volume.",
        ],
      },
      {
        heading: "After qualifying — the value frame",
        lines: [
          "Based on what you're describing, I think there's a real opportunity here. For a practice your size, CCM typically generates $40–80K a year in new revenue without the doctor seeing a single extra patient. We manage the whole program — the nursing calls, the documentation, the billing.",
          "Most of our clients said the same thing before they saw the numbers. Can I put together a quick revenue estimate based on your Medicare panel and send it over today?",
        ],
      },
      {
        heading: "When they say \"we already have something\"",
        lines: [
          "That's great — a lot of practices we work with felt the same way. Can I ask which program you're using? I ask because most practices we encounter are capturing about 30–40% of their eligible patients. If you're already at 80–90%, we probably can't help. But if there's a gap, it's worth 10 minutes.",
          "I'm not here to replace anything — I'm just curious whether what you have is fully covering your eligible Medicare population. When's the last time you did a billing audit on that side?",
        ],
      },
      {
        heading: "When they say \"we're too busy / not a good time\"",
        lines: [
          "I completely get it — practices are incredibly stretched right now. That's actually why most of my clients started looking at this. Our model requires almost zero staff time because we supply the nurses and handle the documentation. When's a better window — early morning or late afternoon tend to work better for most practice managers?",
        ],
      },
    ],
  },
  {
    id: "physician",
    title: "Talking to a physician / doctor",
    icon: Stethoscope,
    color: "#0F6E56",
    hint: "Peer-level, data-first, clinical relevance first — revenue second. Use clinical and billing terminology correctly. Never use generic sales language. Get to the point in under 30 seconds.",
    sections: [
      {
        heading: "Openers",
        lines: [
          "Dr. [Name] — two quick questions. Are you currently billing for chronic care management, and are you capturing all your eligible Medicare patients?",
          "Dr. [Name] — I'll be brief. I work with internal medicine and primary care practices on CMS care management programs. Most practices I talk to are leaving significant Medicare revenue uncollected. Is this something on your radar?",
        ],
      },
      {
        heading: "When the physician says \"we don't have time for this\"",
        lines: [
          "I understand completely — that's exactly why I'm calling. Our program actually reduces administrative burden on your practice, not adds to it. Our nurses make the patient calls, not yours. Could I have 10 minutes with you or your administrator this week?",
        ],
      },
      {
        heading: "When the physician says \"we already do CCM\"",
        lines: [
          "That's great. Are you capturing your QMB patients on APCM? It's a newer CMS program from 2024 that pays $58 per patient per month for your Medicaid dual-eligible patients — most practices haven't set it up yet.",
        ],
      },
      {
        heading: "Revenue framing that resonates with physicians",
        lines: [
          "At your patient volume, CCM and APCM combined typically generate $12,000–$18,000 a month in net revenue after our fee. That's a physician salary equivalent over the year for work your nursing staff is already doing in phone outreach.",
        ],
      },
    ],
  },
  {
    id: "objections",
    title: "Handling common objections",
    icon: AlertOctagon,
    color: "#c2410c",
    hint: "Healthcare-tech-specific responses to the seven most frequent objections EHI agents encounter.",
    sections: [
      {
        heading: "\"We're happy with our current system / provider\"",
        lines: [
          "I'm glad to hear that. Quick question — when did you last do a billing audit? Most practices that are happy with their setup are still leaving 20–30% of their eligible Medicare revenue uncollected. I'm not suggesting you replace anything — I just want to make sure there isn't a gap.",
          "That's exactly what most of our clients said before we ran their numbers. Would you be open to a free billing assessment? If the numbers don't show a meaningful improvement, I'll leave you alone.",
        ],
      },
      {
        heading: "\"We're not interested\"",
        lines: [
          "Completely fair. Can I ask — is that because you have this covered, or just not a priority right now? I want to make sure I'm not reaching back at the wrong time.",
          "I respect that. Before I let you go — are you currently billing for CCM/APCM? Just curious, because if you are, I can share what the average capture rate looks like for practices your size.",
        ],
      },
      {
        heading: "\"Send me information\"",
        lines: [
          "Happy to. Before I do — what's your biggest challenge right now with billing and revenue? I want to make sure I send something specific, not a generic brochure.",
          "I'll send that over today. One quick question — what would need to be true in that information for you to want to have a 10-minute call?",
        ],
      },
      {
        heading: "\"We tried this before and it didn't work\"",
        lines: [
          "I'm sorry to hear that — that's unfortunately common with some programs. Can you tell me what specifically didn't work? The most common issue we hear is that the nurses weren't actually doing the monthly calls, or the documentation wasn't compliant. Our model is different in that way — but I want to understand what happened before I tell you we're better.",
        ],
      },
      {
        heading: "\"Call me back in 6 months\"",
        lines: [
          "I can absolutely do that. Can I ask what changes in 6 months? I want to make sure my follow-up is relevant to whatever's happening then.",
          "I'll put that in my calendar. Quick heads-up — the APCM program from CMS has increased reimbursement rates in 2025. I'd hate for you to miss 6 months of that. Would it be worth a 10-minute call before then just to look at the numbers?",
        ],
      },
      {
        heading: "\"We don't have the staff for this\"",
        lines: [
          "That's the most common concern I hear — and honestly, the reason most practices start working with us. We provide the care coordination nurses. Your staff doesn't make the patient calls, doesn't do the documentation, doesn't handle the billing. We do all of it. Your front desk and MAs are not involved at all.",
        ],
      },
      {
        heading: "\"We're a small practice — this probably doesn't apply to us\"",
        lines: [
          "Actually, smaller practices often see the highest per-physician revenue from these programs because they're not diluting the margin across a large administration. A two-physician primary care practice with 300 Medicare patients can generate $30–50K a year. Worth running the numbers?",
        ],
      },
    ],
  },
  {
    id: "voicemail",
    title: "Voicemail scripts (20-30s max)",
    icon: Voicemail,
    color: "#d97706",
    hint: "Voicemails over 30 seconds are almost never fully listened to. Include one specific number or fact to create curiosity. Never say \"I'm just following up\" or \"I have an exciting opportunity.\"",
    sections: [
      {
        heading: "For front desk / general line",
        lines: [
          "Hi, this is [Name] from Enable Healthcare — [phone number]. I work with primary care offices in [state] on Medicare revenue programs. Most practices I talk with are not collecting on CCM or APCM billing — it typically adds $40–80K a year per physician. If that's relevant to your practice, I'd love five minutes with your administrator. Again, [Name], [phone number]. Thanks.",
        ],
      },
      {
        heading: "For physician / decision-maker",
        lines: [
          "Dr. [Name], this is [Name] from Enable Healthcare — [phone number]. Quick question for you: are you billing for APCM for your Medicaid dual-eligible patients? It's a 2024 CMS program paying $58 per patient per month — most practices haven't set it up yet. Worth a 10-minute call. [Name], [phone number].",
        ],
      },
      {
        heading: "Voicemail rules for agents",
        lines: [
          "Always state your name and number twice — at the beginning and end.",
          "Keep it under 30 seconds — voicemails over 30 seconds are almost never fully listened to.",
          "Include one specific number or fact to create curiosity.",
          "Never say \"I'm just following up\" — it signals no urgency.",
          "Never say \"I have an exciting opportunity\" — it sounds like spam.",
        ],
        noCopy: true,
      },
    ],
  },
];

// Separate "best times" reference — collapsible tip card rendered
// below the categories per the spec.
const BEST_TIMES = {
  best: [
    "8:00 - 9:00 AM — before first patient",
    "12:00 - 1:30 PM — lunch, physician may be available",
    "4:30 - 6:00 PM — post-patient, decision-maker often accessible",
  ],
  worst: [
    "9:30 AM - 11:30 AM — peak patient flow",
    "Monday mornings — post-weekend backlog",
    "Friday afternoons — staff preparing for weekend",
  ],
  days: "Best days: Tuesday, Wednesday, Thursday. Avoid Monday (busy setup) and Friday (low-energy close-out).",
  specialists: "Specialists (cardiology, nephrology, endocrinology) often have more predictable schedules than primary care — their lunch window is more reliable.",
};

function CopyButton({ text, testId }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Couldn't copy — try selecting the text");
    }
  };
  return (
    <button
      data-testid={testId}
      onClick={onCopy}
      title="Copy to clipboard"
      style={{
        flexShrink: 0, padding: "3px 8px", borderRadius: 6,
        border: "0.5px solid #e2e8f0",
        background: copied ? "#dcfce7" : "#fff",
        color: copied ? "#166534" : "#475569",
        fontSize: 10, fontWeight: 600, cursor: "pointer",
        display: "inline-flex", alignItems: "center", gap: 4,
        fontFamily: "var(--font)",
      }}
    >
      <Copy style={{ width: 10, height: 10 }} />
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function Category({ cat, openId, setOpenId }) {
  const isOpen = openId === cat.id;
  const Icon = cat.icon;
  return (
    <div
      data-testid={`talktrack-cat-${cat.id}`}
      style={{
        background: "#fff", border: "0.5px solid #e2e8f0",
        borderRadius: 10, marginBottom: 8, overflow: "hidden",
      }}
    >
      <button
        data-testid={`talktrack-cat-toggle-${cat.id}`}
        aria-expanded={isOpen}
        onClick={() => setOpenId(isOpen ? null : cat.id)}
        style={{
          width: "100%", padding: "12px 16px",
          background: "transparent", border: "none", cursor: "pointer",
          fontFamily: "var(--font)", textAlign: "left",
          display: "flex", alignItems: "center", gap: 10,
        }}
      >
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 28, height: 28, borderRadius: 8,
          background: cat.color + "20", color: cat.color,
        }}>
          <Icon style={{ width: 14, height: 14 }} />
        </span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: "#0f172a" }}>
          {cat.title}
        </span>
        {isOpen ? <ChevronUp style={{ width: 14, height: 14, color: "#64748b" }} /> : <ChevronDown style={{ width: 14, height: 14, color: "#64748b" }} />}
      </button>
      {isOpen && (
        <div style={{ padding: "0 16px 14px 16px", borderTop: "0.5px solid #f1f5f9" }}>
          {cat.hint && (
            <div style={{
              padding: "8px 12px", marginTop: 10, marginBottom: 12,
              borderRadius: 8, background: cat.color + "0d",
              border: `0.5px solid ${cat.color}33`,
              fontSize: 11, color: "#374151", lineHeight: 1.55,
            }}>
              {cat.hint}
            </div>
          )}
          {cat.sections.map((sec, si) => (
            <div key={si} style={{ marginBottom: 14 }} data-testid={`talktrack-section-${cat.id}-${si}`}>
              <div style={{
                fontSize: 10, fontWeight: 700, color: "#475569",
                textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
              }}>{sec.heading}</div>
              {sec.lines.map((line, li) => (
                <div
                  key={li}
                  data-testid={`talktrack-line-${cat.id}-${si}-${li}`}
                  style={{
                    display: "flex", alignItems: "flex-start", gap: 10,
                    padding: "8px 10px", marginBottom: 4,
                    background: "#f8fafc", borderRadius: 8,
                    border: "0.5px solid #f1f5f9",
                    fontSize: 12, lineHeight: 1.55, color: "#0f172a",
                  }}
                >
                  <span style={{ flex: 1 }}>
                    {sec.noCopy ? <span>{line}</span> : <span style={{ fontStyle: "italic" }}>{`"${line}"`}</span>}
                  </span>
                  {!sec.noCopy && (
                    <CopyButton
                      text={line}
                      testId={`talktrack-copy-${cat.id}-${si}-${li}`}
                    />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TalkTrackPanel() {
  const [openId, setOpenId] = useState("opening");
  const [bestTimesOpen, setBestTimesOpen] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(false);

  return (
    <div className="card" style={{ padding: "14px 18px", marginBottom: 14 }} data-testid="talktrack-panel">
      <button
        onClick={() => setPanelCollapsed((v) => !v)}
        data-testid="talktrack-panel-toggle"
        aria-expanded={!panelCollapsed}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10,
          background: "transparent", border: "none", cursor: "pointer",
          fontFamily: "var(--font)", textAlign: "left",
        }}
      >
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 32, height: 32, borderRadius: 10,
          background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
          border: "0.5px solid #f59e0b",
        }}>
          <Sparkles style={{ width: 16, height: 16, color: "#b45309" }} />
        </span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
            Talk track · quick sound bites
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
            7 categories of vetted lines for B2B healthcare cold-calling. Click a line to copy it.
          </div>
        </div>
        <span style={{
          padding: "4px 10px", borderRadius: 6,
          background: "#f1f5f9", color: "#475569",
          fontSize: 11, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          {panelCollapsed ? "Show" : "Hide"}
          {panelCollapsed ? <ChevronDown style={{ width: 12, height: 12 }} /> : <ChevronUp style={{ width: 12, height: 12 }} />}
        </span>
      </button>

      {!panelCollapsed && (
        <div style={{ marginTop: 14 }} data-testid="talktrack-categories">
          {CATEGORIES.map((cat) => (
            <Category key={cat.id} cat={cat} openId={openId} setOpenId={setOpenId} />
          ))}

          {/* Best times to call — collapsible tip card */}
          <div
            data-testid="talktrack-best-times"
            style={{
              background: "#f8fafc", border: "0.5px solid #e2e8f0",
              borderRadius: 10, marginTop: 12, overflow: "hidden",
            }}
          >
            <button
              data-testid="talktrack-best-times-toggle"
              onClick={() => setBestTimesOpen((v) => !v)}
              aria-expanded={bestTimesOpen}
              style={{
                width: "100%", padding: "10px 16px",
                background: "transparent", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 10,
                fontFamily: "var(--font)", textAlign: "left",
              }}
            >
              <span style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                width: 26, height: 26, borderRadius: 8,
                background: "#e0e7ff", color: "#4338ca",
              }}>
                <Clock style={{ width: 13, height: 13 }} />
              </span>
              <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                Best times to call medical clinics
              </span>
              {bestTimesOpen ? <ChevronUp style={{ width: 12, height: 12, color: "#64748b" }} /> : <ChevronDown style={{ width: 12, height: 12, color: "#64748b" }} />}
            </button>
            {bestTimesOpen && (
              <div style={{ padding: "0 16px 14px 16px", borderTop: "0.5px solid #f1f5f9", fontSize: 11, color: "#475569", lineHeight: 1.65 }}>
                <div style={{ marginTop: 10 }}>
                  <strong style={{ color: "#059669" }}>Best windows:</strong>
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {BEST_TIMES.best.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>
                  <strong style={{ color: "#c2410c" }}>Avoid:</strong>
                  <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
                    {BEST_TIMES.worst.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>{BEST_TIMES.days}</div>
                <div style={{ marginTop: 6 }}>{BEST_TIMES.specialists}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
